import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { SelectButton } from 'primereact/selectbutton';
import { TabView, TabPanel } from 'primereact/tabview';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    ArcElement,
    BarController,
    LineController,
    DoughnutController,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import moment from 'moment';
import './dashboard.css';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    ArcElement,
    BarController,
    LineController,
    DoughnutController,
    Title,
    Tooltip,
    Legend,
    Filler
);

const DIMENSION_OPTIONS = [
    { label: 'Counter', value: 'counter', icon: 'pi pi-desktop' },
    { label: 'Institution', value: 'institution', icon: 'pi pi-building' }
];

const VIEW_OPTIONS = [
    { label: 'Graph', value: 'graph', icon: 'pi pi-chart-line' },
    { label: 'Table', value: 'table', icon: 'pi pi-table' }
];

const viewOptionTemplate = (option) => (
    <span className="dashboard-segment-option">
        <i className={option.icon} aria-hidden />
        <span>{option.label}</span>
    </span>
);

const dimensionValueTemplate = (option) => {
    const o =
        option && typeof option === 'object' && option.label
            ? option
            : DIMENSION_OPTIONS.find((x) => x.value === option);
    if (!o) {
        return (
            <span className="dashboard-dropdown-value">
                <i className="pi pi-filter" aria-hidden />
                View
            </span>
        );
    }
    return (
        <span className="dashboard-dropdown-value">
            <i className={o.icon} aria-hidden />
            {o.label}
        </span>
    );
};

const dimensionOptionTemplate = (option) => (
    <span className="dashboard-dropdown-option">
        <i className={option.icon} aria-hidden />
        <span>{option.label}</span>
    </span>
);

/** Reusable canvas chart — destroys on config change */
function ChartBlock({ config, className = '', minHeight = 260 }) {
    const canvasRef = useRef(null);
    const instanceRef = useRef(null);

    useEffect(() => {
        if (!canvasRef.current || !config) return undefined;
        const ctx = canvasRef.current.getContext('2d');
        if (instanceRef.current) {
            instanceRef.current.destroy();
            instanceRef.current = null;
        }
        instanceRef.current = new ChartJS(ctx, config);
        return () => {
            if (instanceRef.current) {
                instanceRef.current.destroy();
                instanceRef.current = null;
            }
        };
    }, [config]);

    return (
        <div className={`dashboard-chart-wrap ${className}`.trim()} style={{ minHeight }}>
            <canvas ref={canvasRef} />
        </div>
    );
}

const commonChartPlugins = {
    legend: {
        position: 'bottom',
        labels: { usePointStyle: true, padding: 14, font: { size: 11 } }
    },
    tooltip: {
        backgroundColor: 'rgba(15, 23, 42, 0.92)',
        padding: 12,
        cornerRadius: 8,
        titleFont: { size: 13 },
        bodyFont: { size: 12 }
    }
};

/** Dummy counter-level data */
const COUNTER_TABLE_ROWS = [
    { id: 1, billNo: 'B-10421', time: '10:12', items: 4, gross: 1240, discount: 40, tax: 54, net: 1254, mop: 'UPI' },
    { id: 2, billNo: 'B-10422', time: '11:05', items: 2, gross: 560, discount: 0, tax: 28, net: 588, mop: 'Card' },
    { id: 3, billNo: 'B-10423', time: '12:40', items: 6, gross: 2100, discount: 100, tax: 100, net: 2100, mop: 'Cash' },
    { id: 4, billNo: 'B-10424', time: '14:18', items: 3, gross: 890, discount: 0, tax: 44.5, net: 934.5, mop: 'UPI' },
    { id: 5, billNo: 'B-10425', time: '15:55', items: 5, gross: 1680, discount: 80, tax: 80, net: 1680, mop: 'Wallet' },
    { id: 6, billNo: 'B-10426', time: '17:22', items: 1, gross: 220, discount: 0, tax: 11, net: 231, mop: 'Cash' }
];

const COUNTER_DAILY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const COUNTER_SALES_SERIES = [12400, 15800, 14200, 17600, 19200, 22400, 18600];
const COUNTER_BILLS_SERIES = [118, 142, 128, 155, 168, 201, 165];
const COUNTER_GROSS_STACK = [13200, 16800, 15100, 18500, 20100, 23600, 19800];
const COUNTER_DISCOUNT_STACK = [800, 1000, 900, 900, 900, 1200, 1200];
const COUNTER_TAX_STACK = [660, 840, 755, 925, 1005, 1180, 990];

const HOURLY_LABELS = ['9a', '10a', '11a', '12p', '1p', '2p', '3p', '4p', '5p', '6p', '7p', '8p', '9p'];
const HOURLY_NET_SALES = [42, 78, 95, 210, 185, 120, 88, 102, 145, 198, 165, 132, 68];

const MOP_LABELS = ['UPI', 'Card', 'Cash', 'Wallet', 'Credit'];
const MOP_PERCENT = [38, 24, 18, 12, 8];

const SALES_DAILY_TABLE = [
    { id: 1, date: '28 Mar', net: 118400, gross: 126200, bills: 112, avgTicket: 498 },
    { id: 2, date: '29 Mar', net: 124000, gross: 132000, bills: 118, avgTicket: 518 },
    { id: 3, date: '30 Mar', net: 131200, gross: 139800, bills: 124, avgTicket: 522 },
    { id: 4, date: '31 Mar', net: 128900, gross: 136500, bills: 121, avgTicket: 506 },
    { id: 5, date: '01 Apr', net: 135600, gross: 143200, bills: 128, avgTicket: 531 },
    { id: 6, date: '02 Apr', net: 129800, gross: 137400, bills: 122, avgTicket: 519 },
    { id: 7, date: '03 Apr', net: 132400, gross: 140100, bills: 125, avgTicket: 528 }
];

const ITEM_WISE_ROWS = [
    { id: 1, item: 'Masala Dosa', category: 'South Indian', qty: 156, amount: 46800, share: 12.4 },
    { id: 2, item: 'Paneer Tikka', category: 'North Indian', qty: 98, amount: 44100, share: 11.7 },
    { id: 3, item: 'Cold Coffee', category: 'Beverages', qty: 212, amount: 38160, share: 10.1 },
    { id: 4, item: 'Veg Fried Rice', category: 'Chinese', qty: 134, amount: 40200, share: 10.6 },
    { id: 5, item: 'Idli Vada Combo', category: 'South Indian', qty: 189, amount: 37800, share: 10.0 },
    { id: 6, item: 'Chocolate Brownie', category: 'Desserts', qty: 76, amount: 22800, share: 6.0 }
];

const CATEGORY_WISE_ROWS = [
    { id: 1, category: 'South Indian', amount: 128400, qty: 412, pct: 28.5 },
    { id: 2, category: 'North Indian', amount: 96400, qty: 268, pct: 21.4 },
    { id: 3, category: 'Beverages', amount: 82200, qty: 540, pct: 18.2 },
    { id: 4, category: 'Chinese', amount: 61800, qty: 198, pct: 13.7 },
    { id: 5, category: 'Desserts', amount: 40200, qty: 124, pct: 8.9 },
    { id: 6, category: 'Snacks', amount: 52800, qty: 310, pct: 9.3 }
];

const TOP_ITEM_LABELS = ITEM_WISE_ROWS.slice(0, 5).map((r) => r.item.split(' ')[0]);
const TOP_ITEM_AMOUNTS = ITEM_WISE_ROWS.slice(0, 5).map((r) => Math.round(r.amount / 1000));
const CATEGORY_LABELS_SHORT = CATEGORY_WISE_ROWS.map((r) => r.category.split(' ')[0]);
const CATEGORY_AMOUNTS_K = CATEGORY_WISE_ROWS.map((r) => Math.round(r.amount / 1000));

const BILLS_HOURLY_COUNTS = [8, 14, 22, 38, 42, 28, 18, 24, 36, 48, 44, 32, 14];

const COUNTER_TAB_STATS = {
    sales: [
        { label: 'Net sales', value: '₹12.84L', sub: '+6.1% vs last week', key: 'ns' },
        { label: 'Gross billing', value: '₹13.72L', sub: '+5.4%', key: 'gr' },
        { label: 'Total bills', value: '847', sub: '+4.2%', key: 'tb' },
        { label: 'Avg. ticket', value: '₹518', sub: '+1.8%', key: 'at' }
    ],
    bills: [
        { label: 'Total bills', value: '847', sub: '+4.2% vs last week', key: 'tbi' },
        { label: 'Cancelled / void', value: '12', sub: '1.4% of bills', key: 'cv' },
        { label: 'Avg. items / bill', value: '3.4', sub: '+0.2 vs LY', key: 'aib' },
        { label: 'Peak window', value: '1 – 2 pm', sub: '48 bills (dummy)', key: 'pk' }
    ],
    itemWise: [
        { label: 'Line items sold', value: '2,864', sub: '+3.8%', key: 'li' },
        { label: 'Unique SKUs', value: '142', sub: '8 new this month', key: 'sku' },
        { label: 'Top item share', value: '12.4%', sub: 'Masala Dosa', key: 'tis' },
        { label: 'Qty (all items)', value: '1,248', sub: '+6.1%', key: 'qty' }
    ],
    categoryWise: [
        { label: 'Active categories', value: '6', sub: 'Counter menu', key: 'ac' },
        { label: 'Top category', value: 'S. Indian', sub: '28.5% sales', key: 'tc' },
        { label: 'Food vs beverage', value: '72 / 28', sub: 'Approx. split', key: 'fb' },
        { label: 'Mix vs LY', value: 'Stable', sub: 'Category-wise', key: 'mx' }
    ]
};

const tabHeader = (iconClass, label) => (
    <span className="dashboard-tab-header">
        <i className={`${iconClass} dashboard-tab-header-icon`} aria-hidden />
        <span className="dashboard-tab-header-text">{label}</span>
    </span>
);

/** Institution dummy */
const INSTITUTION_TABLE_ROWS = [
    { id: 1, name: 'Green Valley School', orders: 48, revenue: 89600, share: 12, status: 'Active' },
    { id: 2, name: 'City Hospital Canteen', orders: 112, revenue: 201500, share: 18, status: 'Active' },
    { id: 3, name: 'Tech Park Tower A', orders: 86, revenue: 142300, share: 15, status: 'Active' },
    { id: 4, name: 'Metro Mall Food Court', orders: 204, revenue: 318400, share: 22, status: 'Active' },
    { id: 5, name: 'Railway Staff Mess', orders: 64, revenue: 78400, share: 10, status: 'Paused' }
];

const INSTITUTION_SHORT = INSTITUTION_TABLE_ROWS.map((r) => r.name.split(' ').slice(0, 2).join(' '));
const INSTITUTION_REVENUE_K = INSTITUTION_TABLE_ROWS.map((r) => Math.round(r.revenue / 1000));
const MONTH_LABELS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
const INSTITUTION_ORDER_TREND = [820, 910, 880, 1020, 1180, 1248];

function currencyTemplate(field) {
    return (row) => (
        <span>
            ₹{Number(row[field]).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
    );
}

function useCounterChartConfigs() {
    return useMemo(
        () => ({
            mixedBarLine: {
                type: 'bar',
                data: {
                    labels: COUNTER_DAILY_LABELS,
                    datasets: [
                        {
                            label: 'Net sales (₹ k)',
                            data: COUNTER_SALES_SERIES.map((v) => Math.round(v / 1000)),
                            backgroundColor: 'rgba(20, 184, 166, 0.72)',
                            borderColor: '#0d9488',
                            borderWidth: 1,
                            borderRadius: 6
                        },
                        {
                            label: 'Bills',
                            data: COUNTER_BILLS_SERIES,
                            type: 'line',
                            yAxisID: 'y1',
                            borderColor: '#6366f1',
                            backgroundColor: 'rgba(99, 102, 241, 0.12)',
                            fill: true,
                            tension: 0.35,
                            pointRadius: 3,
                            pointHoverRadius: 5
                        }
                    ]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: commonChartPlugins,
                    scales: {
                        y: {
                            beginAtZero: true,
                            grid: { color: 'rgba(148, 163, 184, 0.2)' },
                            title: { display: true, text: 'Sales (₹ thousands)' }
                        },
                        y1: {
                            position: 'right',
                            beginAtZero: true,
                            grid: { drawOnChartArea: false },
                            title: { display: true, text: 'Bill count' }
                        },
                        x: { grid: { display: false } }
                    }
                }
            },
            lineHourly: {
                type: 'line',
                data: {
                    labels: HOURLY_LABELS,
                    datasets: [
                        {
                            label: 'Net sales (₹ k)',
                            data: HOURLY_NET_SALES,
                            borderColor: '#0d9488',
                            backgroundColor: 'rgba(20, 184, 166, 0.2)',
                            fill: true,
                            tension: 0.4,
                            pointRadius: 4,
                            pointBackgroundColor: '#fff',
                            pointBorderColor: '#0f766e',
                            pointBorderWidth: 2,
                            borderWidth: 2.5
                        }
                    ]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: commonChartPlugins,
                    scales: {
                        y: {
                            beginAtZero: true,
                            grid: { color: 'rgba(148, 163, 184, 0.2)' },
                            title: { display: true, text: '₹ thousands' }
                        },
                        x: { grid: { display: false } }
                    }
                }
            },
            stackedBar: {
                type: 'bar',
                data: {
                    labels: COUNTER_DAILY_LABELS,
                    datasets: [
                        {
                            label: 'Gross',
                            data: COUNTER_GROSS_STACK.map((g) => Math.round(g / 1000)),
                            backgroundColor: 'rgba(99, 102, 241, 0.75)',
                            borderRadius: 4
                        },
                        {
                            label: 'Discount',
                            data: COUNTER_DISCOUNT_STACK.map((d) => Math.round(d / 1000)),
                            backgroundColor: 'rgba(245, 158, 11, 0.8)',
                            borderRadius: 4
                        },
                        {
                            label: 'Tax',
                            data: COUNTER_TAX_STACK.map((t) => Math.round(t / 1000)),
                            backgroundColor: 'rgba(20, 184, 166, 0.8)',
                            borderRadius: 4
                        }
                    ]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: commonChartPlugins,
                    scales: {
                        x: { stacked: true, grid: { display: false } },
                        y: {
                            stacked: true,
                            beginAtZero: true,
                            grid: { color: 'rgba(148, 163, 184, 0.2)' },
                            title: { display: true, text: '₹ thousands (stacked)' }
                        }
                    }
                }
            },
            doughnutMop: {
                type: 'doughnut',
                data: {
                    labels: MOP_LABELS,
                    datasets: [
                        {
                            data: MOP_PERCENT,
                            backgroundColor: [
                                'rgba(20, 184, 166, 0.9)',
                                'rgba(99, 102, 241, 0.88)',
                                'rgba(245, 158, 11, 0.88)',
                                'rgba(236, 72, 153, 0.85)',
                                'rgba(148, 163, 184, 0.85)'
                            ],
                            borderWidth: 2,
                            borderColor: '#fff',
                            hoverOffset: 8
                        }
                    ]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    cutout: '58%',
                    plugins: {
                        ...commonChartPlugins,
                        legend: { ...commonChartPlugins.legend, position: 'right' },
                        tooltip: {
                            ...commonChartPlugins.tooltip,
                            callbacks: {
                                label: (ctx) => `${ctx.label}: ${ctx.raw}% (share)`
                            }
                        }
                    }
                }
            }
        }),
        []
    );
}

function useCounterBillsChartConfigs() {
    return useMemo(
        () => ({
            billsByDay: {
                type: 'line',
                data: {
                    labels: COUNTER_DAILY_LABELS,
                    datasets: [
                        {
                            label: 'Bill count',
                            data: COUNTER_BILLS_SERIES,
                            borderColor: '#6366f1',
                            backgroundColor: 'rgba(99, 102, 241, 0.12)',
                            fill: true,
                            tension: 0.35,
                            pointRadius: 4,
                            borderWidth: 2.5
                        }
                    ]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: commonChartPlugins,
                    scales: {
                        y: {
                            beginAtZero: true,
                            grid: { color: 'rgba(148, 163, 184, 0.2)' },
                            title: { display: true, text: 'Bills' }
                        },
                        x: { grid: { display: false } }
                    }
                }
            },
            billsByHour: {
                type: 'bar',
                data: {
                    labels: HOURLY_LABELS,
                    datasets: [
                        {
                            label: 'Bills (count)',
                            data: BILLS_HOURLY_COUNTS,
                            backgroundColor: 'rgba(99, 102, 241, 0.72)',
                            borderColor: '#4f46e5',
                            borderWidth: 1,
                            borderRadius: 6
                        }
                    ]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: commonChartPlugins,
                    scales: {
                        y: {
                            beginAtZero: true,
                            grid: { color: 'rgba(148, 163, 184, 0.2)' },
                            title: { display: true, text: 'Count' }
                        },
                        x: { grid: { display: false } }
                    }
                }
            },
            billsVsNet: {
                type: 'bar',
                data: {
                    labels: COUNTER_DAILY_LABELS,
                    datasets: [
                        {
                            label: 'Bills',
                            data: COUNTER_BILLS_SERIES,
                            backgroundColor: 'rgba(99, 102, 241, 0.65)',
                            borderRadius: 6,
                            yAxisID: 'y'
                        },
                        {
                            label: 'Net sales (₹ k)',
                            data: COUNTER_SALES_SERIES.map((v) => Math.round(v / 1000)),
                            backgroundColor: 'rgba(20, 184, 166, 0.65)',
                            borderRadius: 6,
                            yAxisID: 'y1'
                        }
                    ]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: commonChartPlugins,
                    scales: {
                        y: {
                            type: 'linear',
                            position: 'left',
                            beginAtZero: true,
                            title: { display: true, text: 'Bills' },
                            grid: { color: 'rgba(148, 163, 184, 0.15)' }
                        },
                        y1: {
                            type: 'linear',
                            position: 'right',
                            beginAtZero: true,
                            grid: { drawOnChartArea: false },
                            title: { display: true, text: '₹ k' }
                        },
                        x: { grid: { display: false } }
                    }
                }
            },
            doughnutMop: {
                type: 'doughnut',
                data: {
                    labels: MOP_LABELS,
                    datasets: [
                        {
                            data: MOP_PERCENT,
                            backgroundColor: [
                                'rgba(20, 184, 166, 0.9)',
                                'rgba(99, 102, 241, 0.88)',
                                'rgba(245, 158, 11, 0.88)',
                                'rgba(236, 72, 153, 0.85)',
                                'rgba(148, 163, 184, 0.85)'
                            ],
                            borderWidth: 2,
                            borderColor: '#fff',
                            hoverOffset: 8
                        }
                    ]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    cutout: '58%',
                    plugins: {
                        ...commonChartPlugins,
                        legend: { ...commonChartPlugins.legend, position: 'right' },
                        tooltip: {
                            ...commonChartPlugins.tooltip,
                            callbacks: {
                                label: (ctx) => `${ctx.label}: ${ctx.raw}%`
                            }
                        }
                    }
                }
            }
        }),
        []
    );
}

function useCounterItemChartConfigs() {
    return useMemo(
        () => ({
            topItemsBar: {
                type: 'bar',
                data: {
                    labels: TOP_ITEM_LABELS,
                    datasets: [
                        {
                            label: 'Amount (₹ k)',
                            data: TOP_ITEM_AMOUNTS,
                            backgroundColor: 'rgba(20, 184, 166, 0.78)',
                            borderColor: '#0d9488',
                            borderWidth: 1,
                            borderRadius: 8
                        }
                    ]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: commonChartPlugins,
                    scales: {
                        y: {
                            beginAtZero: true,
                            grid: { color: 'rgba(148, 163, 184, 0.2)' },
                            title: { display: true, text: '₹ thousands' }
                        },
                        x: { grid: { display: false } }
                    }
                }
            },
            itemQtyLine: {
                type: 'line',
                data: {
                    labels: COUNTER_DAILY_LABELS,
                    datasets: [
                        {
                            label: 'Qty sold (all items)',
                            data: [420, 458, 441, 465, 492, 470, 488],
                            borderColor: '#f59e0b',
                            backgroundColor: 'rgba(245, 158, 11, 0.15)',
                            fill: true,
                            tension: 0.35,
                            borderWidth: 2.5,
                            pointRadius: 3
                        }
                    ]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: commonChartPlugins,
                    scales: {
                        y: {
                            beginAtZero: true,
                            grid: { color: 'rgba(148, 163, 184, 0.2)' },
                            title: { display: true, text: 'Quantity' }
                        },
                        x: { grid: { display: false } }
                    }
                }
            },
            itemShareDoughnut: {
                type: 'doughnut',
                data: {
                    labels: ITEM_WISE_ROWS.slice(0, 5).map((r) => r.item),
                    datasets: [
                        {
                            data: ITEM_WISE_ROWS.slice(0, 5).map((r) => r.share),
                            backgroundColor: [
                                'rgba(20, 184, 166, 0.9)',
                                'rgba(99, 102, 241, 0.88)',
                                'rgba(245, 158, 11, 0.88)',
                                'rgba(236, 72, 153, 0.85)',
                                'rgba(14, 165, 233, 0.85)'
                            ],
                            borderWidth: 2,
                            borderColor: '#fff',
                            hoverOffset: 8
                        }
                    ]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    cutout: '55%',
                    plugins: {
                        ...commonChartPlugins,
                        legend: { ...commonChartPlugins.legend, position: 'right' },
                        tooltip: {
                            ...commonChartPlugins.tooltip,
                            callbacks: {
                                label: (ctx) => `${ctx.label}: ${ctx.raw}% of sales`
                            }
                        }
                    }
                }
            },
            itemCategoryBar: {
                type: 'bar',
                data: {
                    labels: CATEGORY_LABELS_SHORT,
                    datasets: [
                        {
                            label: 'Item sales (₹ k)',
                            data: CATEGORY_AMOUNTS_K,
                            backgroundColor: [
                                'rgba(20, 184, 166, 0.75)',
                                'rgba(99, 102, 241, 0.75)',
                                'rgba(245, 158, 11, 0.75)',
                                'rgba(236, 72, 153, 0.72)',
                                'rgba(14, 165, 233, 0.72)',
                                'rgba(100, 116, 139, 0.72)'
                            ],
                            borderRadius: 8
                        }
                    ]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: commonChartPlugins,
                    indexAxis: 'y',
                    scales: {
                        x: {
                            beginAtZero: true,
                            grid: { color: 'rgba(148, 163, 184, 0.2)' },
                            title: { display: true, text: '₹ k' }
                        },
                        y: { grid: { display: false } }
                    }
                }
            }
        }),
        []
    );
}

function useCounterCategoryChartConfigs() {
    return useMemo(
        () => ({
            categoryAmountHBar: {
                type: 'bar',
                data: {
                    labels: CATEGORY_WISE_ROWS.map((r) => r.category),
                    datasets: [
                        {
                            label: 'Sales (₹ k)',
                            data: CATEGORY_AMOUNTS_K,
                            backgroundColor: [
                                'rgba(20, 184, 166, 0.85)',
                                'rgba(99, 102, 241, 0.85)',
                                'rgba(245, 158, 11, 0.85)',
                                'rgba(236, 72, 153, 0.85)',
                                'rgba(14, 165, 233, 0.85)',
                                'rgba(100, 116, 139, 0.82)'
                            ],
                            borderRadius: 8,
                            borderSkipped: false
                        }
                    ]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: commonChartPlugins,
                    indexAxis: 'y',
                    scales: {
                        x: {
                            beginAtZero: true,
                            grid: { color: 'rgba(148, 163, 184, 0.2)' },
                            title: { display: true, text: '₹ thousands' }
                        },
                        y: { grid: { display: false } }
                    }
                }
            },
            categoryPctDoughnut: {
                type: 'doughnut',
                data: {
                    labels: CATEGORY_WISE_ROWS.map((r) => r.category),
                    datasets: [
                        {
                            data: CATEGORY_WISE_ROWS.map((r) => r.pct),
                            backgroundColor: [
                                'rgba(20, 184, 166, 0.9)',
                                'rgba(99, 102, 241, 0.88)',
                                'rgba(245, 158, 11, 0.88)',
                                'rgba(236, 72, 153, 0.85)',
                                'rgba(14, 165, 233, 0.85)',
                                'rgba(100, 116, 139, 0.85)'
                            ],
                            borderWidth: 2,
                            borderColor: '#fff',
                            hoverOffset: 8
                        }
                    ]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    cutout: '58%',
                    plugins: {
                        ...commonChartPlugins,
                        legend: { ...commonChartPlugins.legend, position: 'right' },
                        tooltip: {
                            ...commonChartPlugins.tooltip,
                            callbacks: {
                                label: (ctx) => `${ctx.label}: ${ctx.raw}%`
                            }
                        }
                    }
                }
            },
            categoryQtyBar: {
                type: 'bar',
                data: {
                    labels: CATEGORY_WISE_ROWS.map((r) => r.category.split(' ')[0]),
                    datasets: [
                        {
                            label: 'Qty sold',
                            data: CATEGORY_WISE_ROWS.map((r) => r.qty),
                            backgroundColor: 'rgba(20, 184, 166, 0.72)',
                            borderRadius: 8
                        }
                    ]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: commonChartPlugins,
                    scales: {
                        y: {
                            beginAtZero: true,
                            grid: { color: 'rgba(148, 163, 184, 0.2)' },
                            title: { display: true, text: 'Units' }
                        },
                        x: { grid: { display: false } }
                    }
                }
            },
            categoryTrendLine: {
                type: 'line',
                data: {
                    labels: ['W1', 'W2', 'W3', 'W4'],
                    datasets: CATEGORY_WISE_ROWS.slice(0, 4).map((r, i) => ({
                        label: r.category.split(' ')[0],
                        data: [
                            Math.round(r.amount * 0.22),
                            Math.round(r.amount * 0.24),
                            Math.round(r.amount * 0.26),
                            Math.round(r.amount * 0.28)
                        ].map((v) => Math.round(v / 1000)),
                        borderColor: ['#14b8a6', '#6366f1', '#f59e0b', '#ec4899'][i],
                        backgroundColor: 'transparent',
                        tension: 0.35,
                        pointRadius: 3,
                        borderWidth: 2
                    }))
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: commonChartPlugins,
                    scales: {
                        y: {
                            beginAtZero: true,
                            grid: { color: 'rgba(148, 163, 184, 0.2)' },
                            title: { display: true, text: '₹ k (weekly)' }
                        },
                        x: { grid: { display: false } }
                    }
                }
            }
        }),
        []
    );
}

function useInstitutionChartConfigs() {
    return useMemo(
        () => ({
            horizontalBar: {
                type: 'bar',
                data: {
                    labels: INSTITUTION_SHORT,
                    datasets: [
                        {
                            label: 'Revenue (₹ k)',
                            data: INSTITUTION_REVENUE_K,
                            backgroundColor: [
                                'rgba(20, 184, 166, 0.85)',
                                'rgba(99, 102, 241, 0.85)',
                                'rgba(245, 158, 11, 0.85)',
                                'rgba(236, 72, 153, 0.85)',
                                'rgba(14, 165, 233, 0.85)'
                            ],
                            borderRadius: 8,
                            borderSkipped: false
                        }
                    ]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: commonChartPlugins,
                    indexAxis: 'y',
                    scales: {
                        x: {
                            beginAtZero: true,
                            grid: { color: 'rgba(148, 163, 184, 0.2)' },
                            title: { display: true, text: '₹ thousands' }
                        },
                        y: { grid: { display: false } }
                    }
                }
            },
            lineOrders: {
                type: 'line',
                data: {
                    labels: MONTH_LABELS_SHORT,
                    datasets: [
                        {
                            label: 'Linked orders',
                            data: INSTITUTION_ORDER_TREND,
                            borderColor: '#6366f1',
                            backgroundColor: 'rgba(99, 102, 241, 0.12)',
                            fill: true,
                            tension: 0.35,
                            pointRadius: 5,
                            pointHoverRadius: 7,
                            borderWidth: 3
                        }
                    ]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: commonChartPlugins,
                    scales: {
                        y: {
                            beginAtZero: true,
                            grid: { color: 'rgba(148, 163, 184, 0.2)' },
                            title: { display: true, text: 'Orders' }
                        },
                        x: { grid: { display: false } }
                    }
                }
            },
            groupedBar: {
                type: 'bar',
                data: {
                    labels: INSTITUTION_SHORT,
                    datasets: [
                        {
                            label: 'Orders',
                            data: INSTITUTION_TABLE_ROWS.map((r) => r.orders),
                            backgroundColor: 'rgba(20, 184, 166, 0.75)',
                            borderRadius: 6,
                            yAxisID: 'y'
                        },
                        {
                            label: 'Rev. share %',
                            data: INSTITUTION_TABLE_ROWS.map((r) => r.share * 4),
                            backgroundColor: 'rgba(245, 158, 11, 0.65)',
                            borderRadius: 6,
                            yAxisID: 'y1'
                        }
                    ]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: commonChartPlugins,
                    scales: {
                        y: {
                            type: 'linear',
                            position: 'left',
                            beginAtZero: true,
                            title: { display: true, text: 'Orders' },
                            grid: { color: 'rgba(148, 163, 184, 0.15)' }
                        },
                        y1: {
                            type: 'linear',
                            position: 'right',
                            beginAtZero: true,
                            grid: { drawOnChartArea: false },
                            title: { display: true, text: 'Share index' }
                        },
                        x: { grid: { display: false } }
                    }
                }
            },
            doughnutShare: {
                type: 'doughnut',
                data: {
                    labels: INSTITUTION_SHORT,
                    datasets: [
                        {
                            data: INSTITUTION_TABLE_ROWS.map((r) => r.share),
                            backgroundColor: [
                                'rgba(20, 184, 166, 0.9)',
                                'rgba(99, 102, 241, 0.88)',
                                'rgba(245, 158, 11, 0.88)',
                                'rgba(236, 72, 153, 0.85)',
                                'rgba(14, 165, 233, 0.85)'
                            ],
                            borderWidth: 2,
                            borderColor: '#fff',
                            hoverOffset: 8
                        }
                    ]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    cutout: '55%',
                    plugins: {
                        ...commonChartPlugins,
                        legend: { ...commonChartPlugins.legend, position: 'right' },
                        tooltip: {
                            ...commonChartPlugins.tooltip,
                            callbacks: {
                                label: (ctx) => `${ctx.label}: ${ctx.raw}% share`
                            }
                        }
                    }
                }
            }
        }),
        []
    );
}

const CounterGraphs = ({ configs }) => (
    <div className="dashboard-charts-grid">
        <div className="dashboard-content-card dashboard-chart-span-2">
            <div className="dashboard-section-title">
                <i className="pi pi-chart-bar text-teal-600" /> Net sales vs bills (mixed bar + line)
            </div>
            <ChartBlock config={configs.mixedBarLine} minHeight={300} />
        </div>
        <div className="dashboard-content-card">
            <div className="dashboard-section-title">
                <i className="pi pi-clock text-teal-600" /> Hourly net sales (line)
            </div>
            <ChartBlock config={configs.lineHourly} minHeight={280} />
        </div>
        <div className="dashboard-content-card">
            <div className="dashboard-section-title">
                <i className="pi pi-chart-pie text-teal-600" /> Mode of payment (doughnut)
            </div>
            <ChartBlock config={configs.doughnutMop} minHeight={280} />
        </div>
        <div className="dashboard-content-card dashboard-chart-span-2">
            <div className="dashboard-section-title">
                <i className="pi pi-th-large text-teal-600" /> Gross, discount & tax by day (stacked bar)
            </div>
            <ChartBlock config={configs.stackedBar} minHeight={300} />
        </div>
    </div>
);

const CounterBillsGraphs = ({ configs }) => (
    <div className="dashboard-charts-grid">
        <div className="dashboard-content-card dashboard-chart-span-2">
            <div className="dashboard-section-title">
                <i className="pi pi-chart-line text-teal-600" /> Bills by day (line)
            </div>
            <ChartBlock config={configs.billsByDay} minHeight={280} />
        </div>
        <div className="dashboard-content-card">
            <div className="dashboard-section-title">
                <i className="pi pi-chart-bar text-teal-600" /> Bills by hour (bar)
            </div>
            <ChartBlock config={configs.billsByHour} minHeight={280} />
        </div>
        <div className="dashboard-content-card">
            <div className="dashboard-section-title">
                <i className="pi pi-chart-pie text-teal-600" /> MOP mix (doughnut)
            </div>
            <ChartBlock config={configs.doughnutMop} minHeight={280} />
        </div>
        <div className="dashboard-content-card dashboard-chart-span-2">
            <div className="dashboard-section-title">
                <i className="pi pi-sliders-h text-teal-600" /> Bills vs net sales (grouped bar)
            </div>
            <ChartBlock config={configs.billsVsNet} minHeight={300} />
        </div>
    </div>
);

const CounterItemGraphs = ({ configs }) => (
    <div className="dashboard-charts-grid">
        <div className="dashboard-content-card dashboard-chart-span-2">
            <div className="dashboard-section-title">
                <i className="pi pi-chart-bar text-teal-600" /> Top items by amount (bar)
            </div>
            <ChartBlock config={configs.topItemsBar} minHeight={300} />
        </div>
        <div className="dashboard-content-card">
            <div className="dashboard-section-title">
                <i className="pi pi-chart-line text-teal-600" /> Qty sold trend (line)
            </div>
            <ChartBlock config={configs.itemQtyLine} minHeight={280} />
        </div>
        <div className="dashboard-content-card">
            <div className="dashboard-section-title">
                <i className="pi pi-chart-pie text-teal-600" /> Top items share (doughnut)
            </div>
            <ChartBlock config={configs.itemShareDoughnut} minHeight={280} />
        </div>
        <div className="dashboard-content-card dashboard-chart-span-2">
            <div className="dashboard-section-title">
                <i className="pi pi-list text-teal-600" /> Sales by category — item view (horizontal bar)
            </div>
            <ChartBlock config={configs.itemCategoryBar} minHeight={300} />
        </div>
    </div>
);

const CounterCategoryGraphs = ({ configs }) => (
    <div className="dashboard-charts-grid">
        <div className="dashboard-content-card dashboard-chart-span-2">
            <div className="dashboard-section-title">
                <i className="pi pi-chart-bar text-teal-600" /> Sales by category (horizontal bar)
            </div>
            <ChartBlock config={configs.categoryAmountHBar} minHeight={300} />
        </div>
        <div className="dashboard-content-card">
            <div className="dashboard-section-title">
                <i className="pi pi-chart-pie text-teal-600" /> Category mix % (doughnut)
            </div>
            <ChartBlock config={configs.categoryPctDoughnut} minHeight={280} />
        </div>
        <div className="dashboard-content-card">
            <div className="dashboard-section-title">
                <i className="pi pi-chart-bar text-teal-600" /> Qty by category (bar)
            </div>
            <ChartBlock config={configs.categoryQtyBar} minHeight={280} />
        </div>
        <div className="dashboard-content-card dashboard-chart-span-2">
            <div className="dashboard-section-title">
                <i className="pi pi-chart-line text-teal-600" /> Weekly category trend (multi-line)
            </div>
            <ChartBlock config={configs.categoryTrendLine} minHeight={300} />
        </div>
    </div>
);

const InstitutionGraphs = ({ configs }) => (
    <div className="dashboard-charts-grid">
        <div className="dashboard-content-card dashboard-chart-span-2">
            <div className="dashboard-section-title">
                <i className="pi pi-chart-bar text-teal-600" /> Revenue by institution (horizontal bar)
            </div>
            <ChartBlock config={configs.horizontalBar} minHeight={300} />
        </div>
        <div className="dashboard-content-card">
            <div className="dashboard-section-title">
                <i className="pi pi-chart-line text-teal-600" /> Orders trend (line)
            </div>
            <ChartBlock config={configs.lineOrders} minHeight={280} />
        </div>
        <div className="dashboard-content-card">
            <div className="dashboard-section-title">
                <i className="pi pi-chart-pie text-teal-600" /> Revenue share (doughnut)
            </div>
            <ChartBlock config={configs.doughnutShare} minHeight={280} />
        </div>
        <div className="dashboard-content-card dashboard-chart-span-2">
            <div className="dashboard-section-title">
                <i className="pi pi-sliders-h text-teal-600" /> Orders vs share index (grouped bar)
            </div>
            <ChartBlock config={configs.groupedBar} minHeight={300} />
        </div>
    </div>
);

const Dashboard = () => {
    const today = new Date();
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);

    const [fromDate, setFromDate] = useState(weekAgo);
    const [toDate, setToDate] = useState(today);
    const [dimension, setDimension] = useState('counter');
    const [viewMode, setViewMode] = useState('graph');
    const [lastRefresh, setLastRefresh] = useState(() => new Date());

    const counterConfigs = useCounterChartConfigs();
    const counterBillsConfigs = useCounterBillsChartConfigs();
    const counterItemConfigs = useCounterItemChartConfigs();
    const counterCategoryConfigs = useCounterCategoryChartConfigs();
    const institutionConfigs = useInstitutionChartConfigs();

    const institutionStats = useMemo(
        () => [
            { label: 'Institutions', value: '24', sub: '3 new this month', key: 'inst' },
            { label: 'Linked orders', value: '1,248', sub: '+9.4%', key: 'orders' },
            { label: 'Gross billing', value: '₹42.6L', sub: '+5.2%', key: 'gross' },
            { label: 'Settlement pending', value: '₹1.05L', sub: '4 invoices', key: 'pend' }
        ],
        []
    );

    const dateRangeLabel = useMemo(() => {
        const a = moment(fromDate).format('DD MMM YYYY');
        const b = moment(toDate).format('DD MMM YYYY');
        return `${a} — ${b}`;
    }, [fromDate, toDate]);

    const lastRefreshLabel = useMemo(() => moment(lastRefresh).format('DD MMM YYYY, h:mm a'), [lastRefresh]);

    const headerQuickStats = useMemo(
        () =>
            dimension === 'institution'
                ? [
                      { key: 'h1', icon: 'pi pi-building', label: 'Institutions', value: '24', hint: 'linked' },
                      { key: 'h2', icon: 'pi pi-shopping-cart', label: 'Orders', value: '1,248', hint: '+9.4%' },
                      { key: 'h3', icon: 'pi pi-wallet', label: 'Gross bill', value: '₹42.6L', hint: 'range' },
                      { key: 'h4', icon: 'pi pi-clock', label: 'Pending', value: '₹1.05L', hint: '4 inv.' }
                  ]
                : [
                      { key: 'c1', icon: 'pi pi-chart-line', label: 'Net sales', value: '₹12.84L', hint: '+6.1%' },
                      { key: 'c2', icon: 'pi pi-file', label: 'Bills', value: '847', hint: '+4.2%' },
                      { key: 'c3', icon: 'pi pi-box', label: 'Line items', value: '2,864', hint: 'qty' },
                      { key: 'c4', icon: 'pi pi-chart-pie', label: 'Avg. ticket', value: '₹518', hint: '+1.8%' }
                  ],
        [dimension]
    );

    const onHeaderRefresh = () => setLastRefresh(new Date());

    return (
        <div className={`dashboard-page dashboard-page--${dimension}`}>
            <div className="dashboard-hero">
                <div className="dashboard-hero-bg" aria-hidden />
                <div className="dashboard-hero-pattern" aria-hidden />
                <div className="dashboard-hero-content">
                    <div className="dashboard-hero-top">
                        <div className="dashboard-title-block">
                            <div className="dashboard-hero-badges">
                                <Tag value="Live" severity="success" className="dashboard-hero-tag" />
                                <Tag value={dimension === 'counter' ? 'Counter POS' : 'Institution'} className="dashboard-hero-tag dashboard-hero-tag-muted" />
                                <span className="dashboard-hero-meta-pill">
                                    <i className="pi pi-sync" aria-hidden />
                                    {lastRefreshLabel}
                                </span>
                            </div>
                            <h1 className="dashboard-hero-heading">
                                <span className="dashboard-hero-heading-icon" aria-hidden>
                                    <i className="pi pi-chart-bar" />
                                </span>
                                Operations dashboard
                            </h1>
                            <p className="dashboard-hero-subtitle">
                                Snapshot for <strong>{dateRangeLabel}</strong>. Compare performance, bills, items and
                                categories — or switch to <strong>Institution</strong> for linked outlets.
                            </p>
                        </div>
                        <div className="dashboard-hero-right">
                            <div className="dashboard-hero-actions">
                                <Button
                                    type="button"
                                    icon="pi pi-refresh"
                                    rounded
                                    text
                                    className="dashboard-hero-action-btn"
                                    onClick={onHeaderRefresh}
                                    aria-label="Refresh"
                                    title="Refresh"
                                />
                                <Button
                                    type="button"
                                    label="Export"
                                    icon="pi pi-download"
                                    className="dashboard-hero-action-btn dashboard-hero-action-primary"
                                    onClick={() => {}}
                                />
                                <Button
                                    type="button"
                                    label="Print"
                                    icon="pi pi-print"
                                    className="dashboard-hero-action-btn"
                                    outlined
                                    onClick={() => {}}
                                />
                            </div>
                            <div className="dashboard-filters">
                        <div className="filter-field filter-field-calendar">
                            <span className="filter-label">
                                <i className="pi pi-calendar filter-label-icon" aria-hidden />
                                From
                            </span>
                            <Calendar
                                value={fromDate}
                                onChange={(e) => setFromDate(e.value)}
                                dateFormat="dd/mm/yy"
                                showIcon
                                maxDate={toDate}
                                className="dashboard-calendar"
                                inputClassName="dashboard-calendar-input"
                                panelClassName="dashboard-calendar-panel"
                            />
                        </div>
                        <div className="filter-field filter-field-calendar">
                            <span className="filter-label">
                                <i className="pi pi-calendar filter-label-icon" aria-hidden />
                                To
                            </span>
                            <Calendar
                                value={toDate}
                                onChange={(e) => setToDate(e.value)}
                                dateFormat="dd/mm/yy"
                                showIcon
                                minDate={fromDate}
                                className="dashboard-calendar"
                                inputClassName="dashboard-calendar-input"
                                panelClassName="dashboard-calendar-panel"
                            />
                        </div>
                        <div className="filter-field filter-field-dropdown">
                            <span className="filter-label">
                                <i className="pi pi-filter filter-label-icon" aria-hidden />
                                View
                            </span>
                            <Dropdown
                                value={dimension}
                                options={DIMENSION_OPTIONS}
                                onChange={(e) => setDimension(e.value)}
                                optionLabel="label"
                                optionValue="value"
                                className="dashboard-dropdown"
                                panelClassName="dashboard-dropdown-panel"
                                valueTemplate={dimensionValueTemplate}
                                itemTemplate={dimensionOptionTemplate}
                            />
                        </div>
                    </div>
                        </div>
                    </div>
                    <div className="dashboard-hero-summary-bar" role="list" aria-label="Quick summary">
                        {headerQuickStats.map((s) => (
                            <div key={s.key} className="dashboard-hero-quick-stat" role="listitem">
                                <i className={`${s.icon} dashboard-hero-quick-stat-icon`} aria-hidden />
                                <div className="dashboard-hero-quick-stat-text">
                                    <span className="dashboard-hero-quick-stat-label">{s.label}</span>
                                    <span className="dashboard-hero-quick-stat-value">{s.value}</span>
                                </div>
                                <span className="dashboard-hero-quick-stat-hint">{s.hint}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="dashboard-view-toggle">
                <span className="dashboard-view-toggle-label">Display</span>
                <SelectButton
                    value={viewMode}
                    onChange={(e) => e.value != null && setViewMode(e.value)}
                    options={VIEW_OPTIONS}
                    optionLabel="label"
                    optionValue="value"
                    itemTemplate={viewOptionTemplate}
                    className="dashboard-segment-toggle"
                />
            </div>

            {dimension === 'counter' && (
                <TabView className="dashboard-tabview">
                    <TabPanel header={tabHeader('pi pi-chart-line', 'Sales')}>
                        <div className="dashboard-summary-grid">
                            {COUNTER_TAB_STATS.sales.map((s) => (
                                <div key={s.key} className="dashboard-stat-card teal-accent">
                                    <div className="label">{s.label}</div>
                                    <div className="value">{s.value}</div>
                                    <div className="sub">{s.sub}</div>
                                </div>
                            ))}
                        </div>
                        {viewMode === 'graph' && <CounterGraphs configs={counterConfigs} />}
                        {viewMode === 'table' && (
                            <Card className="dashboard-content-card">
                                <div className="dashboard-section-title">
                                    <i className="pi pi-calendar text-teal-600" /> Daily sales summary (dummy)
                                </div>
                                <DataTable value={SALES_DAILY_TABLE} stripedRows size="small" paginator rows={7} responsiveLayout="scroll">
                                    <Column field="date" header="Date" sortable />
                                    <Column
                                        field="net"
                                        header="Net sales"
                                        body={(row) => <span>₹{Number(row.net).toLocaleString('en-IN')}</span>}
                                        sortable
                                    />
                                    <Column
                                        field="gross"
                                        header="Gross"
                                        body={(row) => <span>₹{Number(row.gross).toLocaleString('en-IN')}</span>}
                                        sortable
                                    />
                                    <Column field="bills" header="Bills" sortable />
                                    <Column
                                        field="avgTicket"
                                        header="Avg. ticket"
                                        body={(row) => <span>₹{Number(row.avgTicket).toLocaleString('en-IN')}</span>}
                                        sortable
                                    />
                                </DataTable>
                            </Card>
                        )}
                    </TabPanel>
                    <TabPanel header={tabHeader('pi pi-file', 'Bills')}>
                        <div className="dashboard-summary-grid">
                            {COUNTER_TAB_STATS.bills.map((s) => (
                                <div key={s.key} className="dashboard-stat-card teal-accent">
                                    <div className="label">{s.label}</div>
                                    <div className="value">{s.value}</div>
                                    <div className="sub">{s.sub}</div>
                                </div>
                            ))}
                        </div>
                        {viewMode === 'graph' && <CounterBillsGraphs configs={counterBillsConfigs} />}
                        {viewMode === 'table' && (
                            <Card className="dashboard-content-card">
                                <div className="dashboard-section-title">
                                    <i className="pi pi-list text-teal-600" /> Bill register (dummy)
                                </div>
                                <DataTable value={COUNTER_TABLE_ROWS} stripedRows size="small" paginator rows={6} responsiveLayout="scroll">
                                    <Column field="billNo" header="Bill #" sortable />
                                    <Column field="time" header="Time" sortable />
                                    <Column field="items" header="Items" sortable />
                                    <Column field="gross" header="Gross" body={currencyTemplate('gross')} sortable />
                                    <Column field="discount" header="Discount" body={currencyTemplate('discount')} />
                                    <Column field="tax" header="Tax" body={currencyTemplate('tax')} />
                                    <Column field="net" header="Net" body={currencyTemplate('net')} sortable />
                                    <Column field="mop" header="MOP" />
                                </DataTable>
                            </Card>
                        )}
                    </TabPanel>
                    <TabPanel header={tabHeader('pi pi-box', 'Item wise')}>
                        <div className="dashboard-summary-grid">
                            {COUNTER_TAB_STATS.itemWise.map((s) => (
                                <div key={s.key} className="dashboard-stat-card teal-accent">
                                    <div className="label">{s.label}</div>
                                    <div className="value">{s.value}</div>
                                    <div className="sub">{s.sub}</div>
                                </div>
                            ))}
                        </div>
                        {viewMode === 'graph' && <CounterItemGraphs configs={counterItemConfigs} />}
                        {viewMode === 'table' && (
                            <Card className="dashboard-content-card">
                                <div className="dashboard-section-title">
                                    <i className="pi pi-shopping-bag text-teal-600" /> Item-wise sales (dummy)
                                </div>
                                <DataTable value={ITEM_WISE_ROWS} stripedRows size="small" paginator rows={6} responsiveLayout="scroll">
                                    <Column field="item" header="Item" sortable style={{ minWidth: '140px' }} />
                                    <Column field="category" header="Category" sortable />
                                    <Column field="qty" header="Qty" sortable />
                                    <Column
                                        field="amount"
                                        header="Amount"
                                        body={(row) => <span>₹{Number(row.amount).toLocaleString('en-IN')}</span>}
                                        sortable
                                    />
                                    <Column
                                        field="share"
                                        header="Sales %"
                                        body={(row) => <span>{row.share}%</span>}
                                        sortable
                                    />
                                </DataTable>
                            </Card>
                        )}
                    </TabPanel>
                    <TabPanel header={tabHeader('pi pi-th-large', 'Category wise')}>
                        <div className="dashboard-summary-grid">
                            {COUNTER_TAB_STATS.categoryWise.map((s) => (
                                <div key={s.key} className="dashboard-stat-card teal-accent">
                                    <div className="label">{s.label}</div>
                                    <div className="value">{s.value}</div>
                                    <div className="sub">{s.sub}</div>
                                </div>
                            ))}
                        </div>
                        {viewMode === 'graph' && <CounterCategoryGraphs configs={counterCategoryConfigs} />}
                        {viewMode === 'table' && (
                            <Card className="dashboard-content-card">
                                <div className="dashboard-section-title">
                                    <i className="pi pi-sitemap text-teal-600" /> Category-wise summary (dummy)
                                </div>
                                <DataTable value={CATEGORY_WISE_ROWS} stripedRows size="small" paginator rows={6} responsiveLayout="scroll">
                                    <Column field="category" header="Category" sortable style={{ minWidth: '160px' }} />
                                    <Column
                                        field="amount"
                                        header="Amount"
                                        body={(row) => <span>₹{Number(row.amount).toLocaleString('en-IN')}</span>}
                                        sortable
                                    />
                                    <Column field="qty" header="Qty sold" sortable />
                                    <Column field="pct" header="Mix %" body={(row) => <span>{row.pct}%</span>} sortable />
                                </DataTable>
                            </Card>
                        )}
                    </TabPanel>
                </TabView>
            )}

            {dimension === 'institution' && (
                <>
                    <div className="dashboard-summary-grid">
                        {institutionStats.map((s) => (
                            <div key={s.key} className="dashboard-stat-card teal-accent">
                                <div className="label">{s.label}</div>
                                <div className="value">{s.value}</div>
                                <div className="sub">{s.sub}</div>
                            </div>
                        ))}
                    </div>
                    {viewMode === 'graph' && <InstitutionGraphs configs={institutionConfigs} />}
                    {viewMode === 'table' && (
                        <Card className="dashboard-content-card">
                            <div className="dashboard-section-title">
                                <i className="pi pi-sitemap text-teal-600" /> Institution — performance (dummy)
                            </div>
                            <DataTable value={INSTITUTION_TABLE_ROWS} stripedRows size="small" paginator rows={5} responsiveLayout="scroll">
                                <Column field="name" header="Institution" sortable style={{ minWidth: '200px' }} />
                                <Column field="orders" header="Orders" sortable />
                                <Column
                                    field="revenue"
                                    header="Revenue"
                                    body={(row) => <span>₹{Number(row.revenue).toLocaleString('en-IN')}</span>}
                                    sortable
                                />
                                <Column field="share" header="Rev. share %" sortable />
                                <Column
                                    field="status"
                                    header="Status"
                                    body={(row) =>
                                        row.status === 'Active' ? (
                                            <span className="dashboard-tag-success">{row.status}</span>
                                        ) : (
                                            <span style={{ color: '#b45309', fontWeight: 600 }}>{row.status}</span>
                                        )
                                    }
                                />
                            </DataTable>
                        </Card>
                    )}
                </>
            )}
        </div>
    );
};

export default Dashboard;
